import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import FinishedProductPicker from "@/components/FinishedProductPicker";
import QCIncomingPicker from "@/components/QCIncomingPicker";
import BackButton from "@/components/BackButton";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

const TYPE_FILTERS = {
  incoming: ["INCOMING"],
  manufacturing: ["IN_PROCESS", "FINISHED_GOODS"],
  returns: ["CUSTOMER_RETURN"],
};
const TYPE_DEFAULTS = {
  incoming: "INCOMING",
  manufacturing: "IN_PROCESS",
  returns: "CUSTOMER_RETURN",
};

export default function QCBucket({ bucket }) {
  const meta = {
    incoming: { title: "Incoming QA", subtitle: "Inspections on purchased goods received via GRN.", owner: "Production Manager" },
    manufacturing: { title: "Manufacturing QA", subtitle: "In-process + finished-goods inspections during/after production.", owner: "Production Manager" },
    returns: { title: "Customer returns QA", subtitle: "Inspections on returned goods.", owner: "Production Manager" },
  }[bucket];
  const typeOptions = TYPE_FILTERS[bucket];

  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());

  function blank() {
    return {
      inspection_type: TYPE_DEFAULTS[bucket],
      product_id: "", product_code: "", product_name: "",
      quantity_inspected: 0,
      rejected_quantity: 0,
      overall_result: "PASS",
      notes: "",
      parameters: [{ name: "Dimensions", expected: "600x600 ±0.5", actual: "", pass_fail: "PASS" }],
    };
  }

  const load = async () => {
    const { data } = await api.get("/qc/inspections");
    setItems(data.filter((d) => typeOptions.includes(d.inspection_type)));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bucket]);

  const submit = async () => {
    try {
      await api.post("/qc/inspections", { ...form, quantity_inspected: Number(form.quantity_inspected), rejected_quantity: Number(form.rejected_quantity) });
      toast.success("Inspection recorded"); setOpen(false); setForm(blank()); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setParam = (idx, k, v) => { const next = [...form.parameters]; next[idx] = { ...next[idx], [k]: v }; setForm({ ...form, parameters: next }); };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6" data-testid={`qc-${bucket}-page`}>
      <BackButton />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-black">{meta.title}</h1>
          <p className="text-muted-foreground">{meta.subtitle}</p>
          <div className="text-[10px] uppercase tracking-[0.25em] text-orange-600 font-bold mt-2">Maintained by · {meta.owner}</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid={`qc-${bucket}-new`}><Plus className="w-4 h-4 mr-1" /> New inspection</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>New QC inspection</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Type</Label>
                  <Select value={form.inspection_type} onValueChange={(v) => setForm({ ...form, inspection_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{typeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2"><Label>Overall result</Label>
                  <Select value={form.overall_result} onValueChange={(v) => setForm({ ...form, overall_result: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{["PASS", "FAIL", "REWORK"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>{bucket === "incoming" ? "Item name / PO number / GRN number" : "Finished product (search)"}</Label>
                {bucket === "incoming" ? (
                  <QCIncomingPicker
                    value={form.product_id ? { code: form.product_code, name: form.product_name } : null}
                    onPick={(r) => {
                      const patch = { product_id: r.id, product_code: r.code, product_name: r.name };
                      if (r.type === "PO") { patch.reference_type = "po"; patch.reference_id = r.id; }
                      if (r.type === "GRN") { patch.reference_type = "grn"; patch.reference_id = r.id; }
                      setForm({ ...form, ...patch });
                    }}
                    testId={`qc-${bucket}-incoming-picker`}
                  />
                ) : (
                  <FinishedProductPicker
                    value={form.product_id}
                    onPick={(p) => setForm({ ...form, product_id: p.id, product_code: p.code, product_name: p.name })}
                    testId={`qc-${bucket}-product-picker`}
                    placeholder="Search finished product by name / code / design…"
                  />
                )}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Quantity inspected</Label><Input type="number" value={form.quantity_inspected} onChange={(e) => setForm({ ...form, quantity_inspected: e.target.value })} /></div>
                {form.overall_result !== "PASS" ? (
                  <div className="space-y-2"><Label>Rejected qty</Label><Input type="number" value={form.rejected_quantity} onChange={(e) => setForm({ ...form, rejected_quantity: e.target.value })} /></div>
                ) : null}
              </div>
              <div className="border border-border rounded-md">
                <div className="flex items-center justify-between px-3 py-2 bg-muted/50">
                  <div className="label-overline">Parameters</div>
                  <Button size="sm" variant="outline" onClick={() => setForm({ ...form, parameters: [...form.parameters, { name: "", expected: "", actual: "", pass_fail: "PASS" }] })}>+ Add</Button>
                </div>
                <Table>
                  <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Expected</TableHead><TableHead>Actual</TableHead><TableHead>P/F</TableHead><TableHead></TableHead></TableRow></TableHeader>
                  <TableBody>
                    {form.parameters.map((p, i) => (
                      <TableRow key={i}>
                        <TableCell><Input value={p.name} onChange={(e) => setParam(i, "name", e.target.value)} /></TableCell>
                        <TableCell><Input value={p.expected} onChange={(e) => setParam(i, "expected", e.target.value)} /></TableCell>
                        <TableCell><Input value={p.actual} onChange={(e) => setParam(i, "actual", e.target.value)} /></TableCell>
                        <TableCell>
                          <Select value={p.pass_fail || "PASS"} onValueChange={(v) => setParam(i, "pass_fail", v)}>
                            <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="PASS">PASS</SelectItem><SelectItem value="FAIL">FAIL</SelectItem></SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>{form.parameters.length > 1 && (
                          <button onClick={() => setForm({ ...form, parameters: form.parameters.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                        )}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Input placeholder="Notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              <div className="space-y-2">
                <Label>Inspection report (png/jpg/pdf)</Label>
                <Input type="file" accept="image/png,image/jpeg,application/pdf" onChange={(e) => setForm({ ...form, report_file: e.target.files?.[0] })} data-testid="qc-report-upload" />
                {form.report_file ? <div className="text-xs text-muted-foreground">{form.report_file.name}</div> : null}
              </div>
              <Button onClick={submit}>Save inspection</Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      <div className="surface-card p-4 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Type</TableHead><TableHead>Product</TableHead><TableHead>Inspected</TableHead><TableHead>Rejected</TableHead><TableHead>Result</TableHead><TableHead>By</TableHead></TableRow></TableHeader>
          <TableBody>
            {items.map((i) => (
              <TableRow key={i.id}>
                <TableCell className="font-mono text-xs">{i.number}</TableCell>
                <TableCell><Badge variant="outline" className="font-mono text-[10px]">{i.inspection_type}</Badge></TableCell>
                <TableCell className="text-sm">{i.product_name || "—"}{i.product_code ? <div className="text-[10px] text-muted-foreground font-mono">{i.product_code}</div> : null}</TableCell>
                <TableCell className="font-mono">{i.quantity_inspected}</TableCell>
                <TableCell className="font-mono">{i.rejected_quantity}</TableCell>
                <TableCell><Badge variant="outline" className={`font-mono text-[10px] ${i.overall_result === "PASS" ? "bg-emerald-100 text-emerald-900 border-emerald-200" : i.overall_result === "FAIL" ? "bg-red-100 text-red-900 border-red-200" : "bg-amber-100 text-amber-900 border-amber-200"}`}>{i.overall_result}</Badge></TableCell>
                <TableCell className="text-xs">{i.inspector_email}</TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No inspections yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
